import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import { BotProfile } from "./types";

interface ConnectedWorkflow {
  id: number;
  name: string;
  enabled: boolean;
  workflowStatus: string;
}

interface Props {
  profile: BotProfile;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ArchivePersonaModal({ profile, onConfirm, onCancel }: Props) {
  const api = useApi();
  const [workflows, setWorkflows] = useState<ConnectedWorkflow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.messaging
      .get(`BotProfiles/${profile.id}/connected-workflows`)
      .then((r) => r.json())
      .then((data) => setWorkflows(Array.isArray(data) ? data : []))
      .catch(() => setWorkflows([]))
      .finally(() => setLoading(false));
  }, [api.messaging, profile.id]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-[480px] max-h-[85vh] flex flex-col overflow-hidden mx-4">

        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="fa fa-archive text-amber-600 text-base" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-800">Archive "{profile.name}"?</h2>
            <p className="text-sm text-slate-500 mt-0.5 leading-snug">
              This persona will be hidden and won't be used for new conversations.
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {/* Past conversations note */}
          <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 flex gap-3">
            <i className="fa fa-info-circle text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-700 leading-relaxed">
              <span className="font-semibold">Past conversations aren't affected.</span> Any conversation
              that already used this persona keeps its history intact — this persona's name and avatar
              remain visible in those transcripts.
            </p>
          </div>

          {/* Connected workflows */}
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-2">
              <i className="fa fa-circle-o-notch fa-spin text-xs" />
              Checking connected workflows…
            </div>
          ) : workflows.length > 0 ? (
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">
                <i className="fa fa-sitemap text-amber-500 mr-1.5" />
                Used in {workflows.length} workflow{workflows.length > 1 ? "s" : ""}
              </p>
              <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                These workflows will fall back to the site's default persona after archiving.
                You can re-link them anytime from the workflow editor.
              </p>
              <div className="rounded-xl border border-amber-200 bg-amber-50 divide-y divide-amber-100 max-h-48 overflow-y-auto">
                {workflows.map((wf) => (
                  <div key={wf.id} className="flex items-center gap-3 px-3 py-2.5">
                    <i className={`fa ${wf.enabled ? "fa-play-circle text-green-500" : "fa-pause-circle text-slate-400"} text-xs flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-700 truncate">{wf.name}</p>
                      <p className="text-[10px] text-slate-400 capitalize">{wf.workflowStatus ?? (wf.enabled ? "active" : "disabled")}</p>
                    </div>
                    <a
                      href={`/workflows/${wf.id}`}
                      className="text-[10px] text-cornflower-blue-500 hover:underline flex-shrink-0"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open →
                    </a>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-green-100 bg-green-50 px-4 py-3 flex gap-3">
              <i className="fa fa-check-circle text-green-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-green-700">
                This persona isn't linked to any active workflows — safe to archive.
              </p>
            </div>
          )}

          {/* Default promotion note */}
          {profile.isDefault && (
            <div className="rounded-xl border border-orange-100 bg-orange-50 px-4 py-3 flex gap-3">
              <i className="fa fa-star text-orange-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-orange-700 leading-relaxed">
                This is the <span className="font-semibold">default persona</span>. Archiving it will
                automatically promote the next available persona to default.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold transition-colors flex items-center gap-1.5 shadow-sm"
          >
            <i className="fa fa-archive text-xs" />
            Archive Persona
          </button>
        </div>
      </div>
    </div>
  );
}
